import formValidators from '@/logic/helpers/forms/validators';
import requestAPI from '@/logic/requestBeApi';
import passDataToResponseAlert from '@/logic/helpers/api/passDataToResponseAlert';

interface education {
  name: string;
  started: string;
  ended: null | string;
  country: string;
  highest_degree: string;
  field_of_study: string;
  id?: string;
}

interface career {
  name: string;
  function: string;
  industry: string;
  started: string;
  ended: null;
  country: string;
  id?: string;
}

interface mockEntry {
  started: string;
  ended: string;
}

const optimizeHelpers = {
  async getEntries(self: Record<string, any>, entry: string): Promise<void> {
    const resp = await requestAPI({
      method: 'GET',
      url: `/api/v1/assessors/me/${entry}${entry === 'career' ? 's' : ''}`,
    });

    if (resp.ok) {
      const data: education[] | career[] | [] = await resp.json();

      this.sortEntries(data);

      self[`${entry}s`] = data;
    } else if (resp.status !== 401) {
      self.$message.error(self.$t(`someErrorOccurred`));
    }
  },

  async createEntry(self: Record<string, any>, entry: string): Promise<void> {
    formValidators.validateForm(self, `${entry}Form`);

    if (!self.formIsValid) return;

    self.processingUserDataRequest = true;

    const payload: education | career = self[entry];

    const resp = await requestAPI({
      method: 'POST',
      url: `/api/v1/${entry}${entry === 'career' ? 's' : ''}`,
      payload,
    });

    if (resp.ok) {
      const data = await resp.json();

      self[`${entry}s`].push(Object.assign({ ...payload }, { id: data.id }) as never);

      this.sortEntries(self[`${entry}s`]);

      if (!self.focusOnProgressCircle) self.$message.success(self.$t(`${entry}Added`));

      self.$emit('runGetProfileCompletionProcess');

      this.purgeFormData(self, entry);
    } else {
      const data = await passDataToResponseAlert(resp);
      self.serverResponse = data;
    }

    self.processingUserDataRequest = false;
  },

  async updateEntry(self: Record<string, any>, entry: string, id: string): Promise<void> {
    formValidators.validateForm(self, `${entry}Form`);

    if (!self.formIsValid) return;

    self.processingUserDataRequest = true;

    const payload: education | career = self[entry];

    const resp = await requestAPI({
      method: 'PUT',
      url: `/api/v1/${entry}${entry === 'career' ? 's' : ''}/${id}`,
      payload,
    });

    if (resp.ok) {
      const editedEntry: education | career = Object.assign({ ...payload }, { id });

      self[`${entry}s`] = self[`${entry}s`].filter((el: { id: string }) => el.id !== id);
      self[`${entry}s`].splice(self.entryToEditIdx as number, 0, editedEntry);

      this.sortEntries(self[`${entry}s`]);

      if (!self.focusOnProgressCircle) self.$message.success(self.$t(`${entry}Updated`));

      self.$emit('runGetProfileCompletionProcess');

      this.purgeFormData(self, entry);
    } else {
      const data = await passDataToResponseAlert(resp);
      self.serverResponse = data;
    }

    self.processingUserDataRequest = false;
  },

  async deleteEntry(self: Record<string, any>, entry: string, id: string): Promise<void> {
    self.processingUserDataRequest = true;

    const removeSchoolResponse = await requestAPI({
      method: 'DELETE',
      url: `/api/v1/${entry}${entry === 'career' ? 's' : ''}/${id}`,
    });

    if (removeSchoolResponse.ok) {
      self[`${entry}s`] = self[`${entry}s`].filter((el: { id: string }) => el.id !== id);

      if (!self.focusOnProgressCircle) self.$message.success(self.$t(`${entry}Removed`));

      self.$emit('runGetProfileCompletionProcess');

      this.purgeFormData(self, entry);
    } else {
      const data = await passDataToResponseAlert(removeSchoolResponse);
      self.serverResponse = data;
    }

    self.processingUserDataRequest = false;
  },

  turnOnTheEditMode(self: Record<string, any>, entry: string, idx: number): void {
    self.editModeOn = true;
    self.showForm = true;

    const entryToEdit = { ...(self[`${entry}s`][idx] as education | career) };

    self[`${entry}ToEditId`] = entryToEdit.id as string;
    self[`${entry}ToEditIdx`] = idx;

    delete entryToEdit.id;

    self[entry] = entryToEdit;
  },

  purgeFormData(self: Record<string, any>, entry: string): void {
    self.serverResponse = {};
    self.editModeOn = false;
    self.showForm = false;
    self.$refs[`${entry}Form`].resetFields();
  },

  sortEntries(entries: education[] | career[] | mockEntry[]): void {
    entries.sort((a: education | career | mockEntry, b: education | career | mockEntry) => {
      const year1: number = b.ended ? new Date(b.ended).getFullYear() : Number.MAX_SAFE_INTEGER;
      const year2: number = a.ended ? new Date(a.ended).getFullYear() : Number.MAX_SAFE_INTEGER;

      if (year1 === year2) {
        return new Date(b.started).getFullYear() - new Date(a.started).getFullYear();
      }

      return year1 - year2;
    });
  },
};

export default optimizeHelpers;
